import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, User, UserPlus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const PLAN_LBL = { maly: "Malý servis", velky: "Veľký servis", free_unlimited: "Zadarmo neobmedzený" };
const SUB_LBL = { none: "—", trialing: "Skúšobná", active: "Aktívne", past_due: "Platba zlyhala", canceled: "Zrušené" };
const fmtDT = (iso) => { if (!iso) return "nikdy"; try { return new Date(iso).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export function AdminUsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [credit, setCredit] = useState({});
  const [nu, setNu] = useState({ email: "", password: "", name: "", role: "user", plan: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch { toast.error("Prístup len pre správcu"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try { await api.put(`/admin/users/${id}`, patch); toast.success("Uložené"); load(); }
    catch { toast.error("Nepodarilo sa uložiť"); }
  };
  const activateFree = async (id) => {
    try { await api.post(`/admin/users/${id}/activate-free`); toast.success("Aktivovaný plný účet zadarmo"); load(); }
    catch { toast.error("Nepodarilo sa aktivovať"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Naozaj zmazať účet ${u.email}? Túto akciu nie je možné vrátiť.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Účet zmazaný"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nepodarilo sa zmazať"); }
  };
  const saveCredits = async (id) => {
    const val = credit[id];
    if (val === undefined || val === "") return;
    await update(id, { spz_credits: parseInt(val, 10) });
    setCredit((c) => ({ ...c, [id]: undefined }));
  };
  const createUser = async () => {
    if (!nu.email || nu.password.length < 6) { toast.error("Zadajte e-mail a heslo (min. 6 znakov)"); return; }
    setCreating(true);
    try {
      await api.post("/admin/users", { ...nu, plan: nu.plan || null });
      toast.success("Účet vytvorený");
      setNu({ email: "", password: "", name: "", role: "user", plan: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nepodarilo sa vytvoriť účet"); }
    finally { setCreating(false); }
  };

  const selCls = "h-10 rounded-md border border-border bg-background px-2 text-sm";

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      {/* Add account */}
      <div className="rounded-xl border border-border bg-background/40 p-4">
        <h4 className="mb-3 flex items-center gap-2 font-heading text-base font-bold uppercase"><UserPlus className="h-4 w-4 text-primary" /> Pridať účet</h4>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1"><Label className="text-xs">E-mail</Label><Input value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid="new-user-email" /></div>
          <div className="space-y-1"><Label className="text-xs">Heslo (min. 6)</Label><Input type="text" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} data-testid="new-user-password" /></div>
          <div className="space-y-1"><Label className="text-xs">Meno / servis</Label><Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} data-testid="new-user-name" /></div>
          <div className="space-y-1"><Label className="text-xs">Rola</Label><select className={selCls + " w-full"} value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })} data-testid="new-user-role"><option value="user">Používateľ</option><option value="admin">Správca</option></select></div>
          <div className="space-y-1"><Label className="text-xs">Balíček</Label><select className={selCls + " w-full"} value={nu.plan} onChange={(e) => setNu({ ...nu, plan: e.target.value })} data-testid="new-user-plan"><option value="">— žiadny (demo)</option><option value="maly">Malý servis</option><option value="velky">Veľký servis</option><option value="free_unlimited">Zadarmo neobmedzený</option></select></div>
          <div className="flex items-end"><Button className="min-h-[40px] w-full gap-1.5" onClick={createUser} disabled={creating} data-testid="create-user-button">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Vytvoriť</Button></div>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <>
          {users.map((u) => (
            <div key={u.id} className="rounded-lg border border-border bg-background/40 flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`user-row-${u.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-heading text-lg font-bold">
                  {u.role === "admin" ? <ShieldCheck className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-muted-foreground" />}
                  {u.name || u.email}
                </div>
                <div className="mt-0.5 text-sm text-muted-foreground">{u.email}</div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs">
                  <span className={`rounded-full border px-2 py-0.5 font-semibold ${u.mode === "demo" ? "border-amber-500/40 bg-amber-500/10 text-amber-400" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"}`}>{u.mode === "demo" ? "DEMO" : "PLNÝ PRÍSTUP"}</span>
                  <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">{u.role === "admin" ? "Správca" : "Používateľ"}</span>
                  {u.plan && <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-semibold text-primary">{PLAN_LBL[u.plan] || u.plan}</span>}
                  <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">{SUB_LBL[u.sub_status] || u.sub_status}</span>
                  {u.ai_addon && <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">+AI</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground" data-testid={`user-usage-${u.id}`}>
                  <span>Kredity dnes: <strong className="text-foreground">{u.spz_credits ?? "—"}</strong></span>
                  <span>Kredity spolu: <strong className="text-foreground">{u.spz_used_total || 0}</strong></span>
                  <span>Prihlásení: <strong className="text-foreground">{u.login_count || 0}</strong></span>
                  <span>Naposledy: <strong className="text-foreground">{fmtDT(u.last_login_at)}</strong></span>
                  <span>{u.customers} zák. · {u.jobs} zákaziek · {u.invoices} faktúr</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex flex-wrap justify-end gap-2">
                  {u.role !== "admin" && (u.mode === "demo" ? (
                    <Button size="sm" onClick={() => update(u.id, { mode: "full" })} data-testid={`grant-full-${u.id}`}>Udeliť plný prístup</Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => update(u.id, { mode: "demo" })} data-testid={`set-demo-${u.id}`}>Späť na demo</Button>
                  ))}
                  {u.role !== "admin" && u.plan !== "free_unlimited" && (
                    <Button size="sm" variant="outline" onClick={() => activateFree(u.id)} data-testid={`activate-free-${u.id}`}>Zadarmo neobmedzený</Button>
                  )}
                  {u.role !== "admin" && u.plan === "free_unlimited" && (
                    <Button size="sm" variant="ghost" onClick={() => update(u.id, { plan: null, ai_addon: false, mode: "demo" })} data-testid={`revoke-free-${u.id}`}>Zrušiť zadarmo</Button>
                  )}
                  {u.role !== "admin" && (
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => removeUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
                {u.role !== "admin" && u.plan !== "free_unlimited" && (
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground">Kredity</Label>
                    <Input type="number" className="h-9 w-20 text-center" value={credit[u.id] ?? (u.spz_credits ?? "")} onChange={(e) => setCredit((c) => ({ ...c, [u.id]: e.target.value }))} data-testid={`credit-input-${u.id}`} />
                    <Button size="sm" variant="secondary" className="h-9 gap-1" onClick={() => saveCredits(u.id)} data-testid={`credit-save-${u.id}`}><Check className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {!users.length && <div className="py-12 text-center text-muted-foreground">Žiadni používatelia.</div>}
        </>
      )}
    </div>
  );
}
